import type { Evaluation } from "@/types/evaluation";
import type { JSONContent } from "@tiptap/react";
import { parseFieldDef } from "@/lib/parseFieldDef";

interface CompletionCounts {
  completed: number;
  total: number;
}

/** Returns true if a field value counts as "filled in". */
function isValueFilled(val: unknown): boolean {
  return val !== undefined && val !== null && val !== "";
}

/**
 * Count completed and total fields for a single TipTap field node.
 * Returns null if the node should be skipped (hidden section or parse error).
 */
function countFieldNode(
  node: JSONContent,
  sectionToggles: Record<string, boolean>,
  values: Record<string, unknown>,
): CompletionCounts | null {
  const field = parseFieldDef((node.attrs?.fieldDef as string | undefined) ?? "");
  if (!field) {
    return null;
  }
  const sectionId = node.attrs?.sectionId as string | undefined;
  if (sectionId && !(sectionToggles[sectionId] ?? true)) {
    return null;
  }
  return { completed: isValueFilled(values[field.id]) ? 1 : 0, total: 1 };
}

export function evalCompletion(ev: Evaluation): CompletionCounts {
  if (!ev.unifiedDocument) {
    return { completed: 0, total: 0 };
  }

  const doc = ev.unifiedDocument as JSONContent;
  let completed = 0;
  let total = 0;

  for (const node of doc.content ?? []) {
    if (node.type !== "fieldNode") {
      continue;
    }
    const counts = countFieldNode(node, ev.sectionToggles, ev.values);
    if (counts) {
      total += counts.total;
      completed += counts.completed;
    }
  }

  return { completed, total };
}
